/**
 * Whole-canopy occluder fade controller (feature 2, 2026-07-02).
 *
 * Replaces the "circle punched through leaves" read (the old screen-space
 * hero-porthole cutout) with the WHOLE occluding doodad instance fading
 * toward ghost transparency. Owns:
 *   - a throttled camera→hero raycast scan (a small probe fan around the
 *     hero's body, not just its center — a tree slightly off-center can still
 *     visually cover the silhouette) against the doodad "objects" group,
 *   - a small persistent map of "recently touched" instances easing their
 *     fade value in/out over ~200ms (linear ramp + smoothstep at write time),
 *   - writes into each instance's shared `instanceFade` attribute (see
 *     `world/doodads.ts`) — read by the toon shader's per-instance dither
 *     discard (`toon.ts` canopyFadeUniforms).
 *
 * The scan itself only needs to run at ~20Hz (occlusion state doesn't change
 * faster than a footstep); the fade LERP still advances every frame off `dt`
 * so the transition reads smooth regardless of scan cadence.
 */
import * as THREE from "three";
import { getDoodadFadeAttribute } from "./doodads";

const SCAN_INTERVAL_MS = 50;         // ~20Hz occlusion rescan
const FADE_TIME_MS = 200;            // full fade in/out ramp (~150-250ms target)
const RAY_MARGIN = 0.4;              // must be this much closer than the hero to count as occluding
const PROBE_HALF_WIDTH = 0.55;       // world units either side of the hero centerline
// feet / mid-body / head world-space Y offsets above the ground sample.
const PROBE_HEIGHTS = [0.25, 1.0, 1.7];
// An instance counts as "faded" (for consumers like xray-silhouette.ts, which
// must not also treat an already-ghosted canopy as an x-ray occluder) once
// its eased progress is past the halfway point toward transparent.
const FADE_OCCLUDE_THRESHOLD = 0.5;

interface FadeState {
  mesh: THREE.InstancedMesh;
  instanceId: number;
  current: number; // 0 = solid, 1 = fully faded (linear internal progress)
  target: number;  // 0 or 1, set by the latest scan
}

export interface CanopyFadeController {
  setEnabled(v: boolean): void;
  isEnabled(): boolean;
  /**
   * Per-frame update. `heroFoot` is the hero's world-space GROUND point
   * (sampleHeight, no Y offset — the probe heights add their own). No-ops
   * while disabled or before the doodad "objects" group exists.
   */
  update(dtMs: number, camera: THREE.Camera, heroFoot: THREE.Vector3, objectsRoot: THREE.Object3D | null): void;
  /**
   * True once the given doodad InstancedMesh instance is more than halfway
   * faded toward transparent by the scan above. Consumers (xray-silhouette.ts)
   * use this to skip already-ghosted canopy instances when deciding whether a
   * unit is occluded — otherwise a faded tree still gates on the x-ray gold
   * silhouette, stacking with the dithered leaves into a "tinted model"
   * look that was already rejected once. See xray-silhouette.ts for the fix.
   */
  isInstanceFaded(mesh: THREE.InstancedMesh, instanceId: number): boolean;
}

export function createCanopyFadeController(): CanopyFadeController {
  let enabled = true;
  let scanAccumMs = SCAN_INTERVAL_MS; // fire on the first update() call
  const active = new Map<string, FadeState>();

  const raycaster = new THREE.Raycaster();
  const _origin = new THREE.Vector3();
  const _dir = new THREE.Vector3();
  const _fwd = new THREE.Vector3();
  const _right = new THREE.Vector3();
  const _up = new THREE.Vector3(0, 1, 0);
  const _probes: THREE.Vector3[] = [
    new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(),
    new THREE.Vector3(), new THREE.Vector3(),
  ];

  function keyFor(mesh: THREE.InstancedMesh, id: number): string {
    return `${mesh.id}:${id}`;
  }

  function writeFade(st: FadeState): void {
    const attr = getDoodadFadeAttribute(st.mesh);
    if (!attr) return;
    // Smoothstep-ease the linear internal progress for a nicer in/out feel.
    const t = st.current;
    const eased = t * t * (3 - 2 * t);
    attr.setX(st.instanceId, eased);
    // Narrow the GPU upload to just the one float that changed, instead of
    // the whole per-cell buffer (chunks can hold dozens of instances — see
    // world/doodads.ts CHUNK_SIZE). Multiple instances in the same cell can
    // each add their own range the same frame (a footstep near a cluster) —
    // addUpdateRange accumulates a list rather than overwriting, and
    // WebGLAttributes merges/clears it after the upload each frame.
    attr.addUpdateRange(st.instanceId, 1);
    attr.needsUpdate = true;
  }

  function scanOccluders(camera: THREE.Camera, heroFoot: THREE.Vector3, objectsRoot: THREE.Object3D): void {
    _origin.copy(camera.position);
    camera.getWorldDirection(_fwd);
    _fwd.y = 0;
    if (_fwd.lengthSq() < 1e-6) _fwd.set(0, 0, 1);
    _fwd.normalize();
    _right.crossVectors(_fwd, _up).normalize();

    const midY = heroFoot.y + PROBE_HEIGHTS[1]!;
    _probes[0]!.set(heroFoot.x, heroFoot.y + PROBE_HEIGHTS[0]!, heroFoot.z);
    _probes[1]!.set(heroFoot.x, midY, heroFoot.z);
    _probes[2]!.set(heroFoot.x, heroFoot.y + PROBE_HEIGHTS[2]!, heroFoot.z);
    _probes[3]!.set(heroFoot.x, midY, heroFoot.z).addScaledVector(_right, PROBE_HALF_WIDTH);
    _probes[4]!.set(heroFoot.x, midY, heroFoot.z).addScaledVector(_right, -PROBE_HALF_WIDTH);

    const hitKeys = new Set<string>();
    for (const target of _probes) {
      const dist = _origin.distanceTo(target);
      if (dist < 0.05) continue;
      _dir.copy(target).sub(_origin).multiplyScalar(1 / dist);
      raycaster.set(_origin, _dir);
      raycaster.near = 0.1;
      raycaster.far = dist + RAY_MARGIN;
      const hits = raycaster.intersectObject(objectsRoot, true);
      for (const hit of hits) {
        if (hit.distance >= dist - RAY_MARGIN) continue;
        if (hit.instanceId === undefined) continue;
        const mesh = hit.object as THREE.InstancedMesh;
        if (!getDoodadFadeAttribute(mesh)) continue; // not a fade-eligible doodad primitive
        const key = keyFor(mesh, hit.instanceId);
        hitKeys.add(key);
        let st = active.get(key);
        if (!st) {
          st = { mesh, instanceId: hit.instanceId, current: 0, target: 1 };
          active.set(key, st);
        } else {
          st.target = 1;
        }
      }
    }
    // Anything active but not re-hit this scan starts easing back to solid.
    for (const [key, st] of active) {
      if (!hitKeys.has(key)) st.target = 0;
    }
  }

  return {
    setEnabled(v) {
      enabled = v;
      if (!v) {
        // Snap every tracked instance back to fully solid immediately.
        for (const st of active.values()) {
          st.current = 0;
          st.target = 0;
          writeFade(st);
        }
        active.clear();
      }
    },
    isEnabled() {
      return enabled;
    },
    isInstanceFaded(mesh, instanceId) {
      const st = active.get(keyFor(mesh, instanceId));
      return !!st && st.current >= FADE_OCCLUDE_THRESHOLD;
    },
    update(dtMs, camera, heroFoot, objectsRoot) {
      if (!enabled || !objectsRoot) return;
      scanAccumMs += dtMs;
      if (scanAccumMs >= SCAN_INTERVAL_MS) {
        scanAccumMs = 0;
        scanOccluders(camera, heroFoot, objectsRoot);
      }
      if (active.size === 0) return;
      const step = dtMs / FADE_TIME_MS;
      for (const [key, st] of active) {
        if (st.current < st.target) st.current = Math.min(st.target, st.current + step);
        else if (st.current > st.target) st.current = Math.max(st.target, st.current - step);
        writeFade(st);
        if (st.target === 0 && st.current <= 0) active.delete(key);
      }
    },
  };
}
